import React, { useCallback, useMemo, useState } from "react";
import {
  Box,
  Button,
  HStack,
  Icon,
  IconButton,
  useBoolean,
} from "@chakra-ui/react";
import useTranslate from "hooks/useTranslate";
import useDressingRoom from "hooks/useDressingRoom";
import TraitTile from "components/TraitTile";
import PurchasePopup from "components/PurchasePopup";
import { FaTimes } from "react-icons/fa";
import { BigNumber } from "ethers";
import useAssets from "hooks/useAssets";
import { formatEtherBalance } from "utils/numberUtils";
import StakePopup from "components/StakePopup";
import useTraitSales from "hooks/useTraitSales";
import TraitsList from "components/TraitsList";
import useV2 from "hooks/useV2";
import StakeSuccessPopup from "components/StakeSuccessPopup";

const TraitsBagContainer = () => {
  const translate = useTranslate();
  const { traitSalesByTokenId, fetchTraitSales } = useTraitSales();
  const { tokenId } = useV2();
  const { v2sInWallet, readHoneyBalance, fetchTraitsInWallet, honeyBalance } =
    useAssets();
  const { changedTraits, originalTraits, removeTrait } = useDressingRoom();
  const [selectedTraits, setSelectedTraits] = useState<number[]>([]);
  const [isPurchasePopupOpen, setIsPurchasePopupOpen] = useBoolean();
  const [isStakePopupOpen, setIsStakePopupOpen] = useBoolean();
  const [isStakeSuccessPopupOpen, setIsStakeSuccessPopupOpen] = useBoolean();

  const totalPrice = useMemo(
    () =>
      changedTraits?.reduce((prev, tokenId) => {
        const traitSale = traitSalesByTokenId[tokenId];
        return traitSale ? prev.add(traitSale.price) : prev;
      }, BigNumber.from("0")),
    [changedTraits, traitSalesByTokenId]
  );

  const isOwner =
    tokenId && v2sInWallet?.length && v2sInWallet.includes(tokenId);
  const isMultiple = (changedTraits?.length || 0) > 1;
  const isFree = totalPrice?.eq("0") ?? false;
  const isTooExpensiveOrUnknown =
    !honeyBalance || !totalPrice || honeyBalance.lt(totalPrice);

  const isOneSoldOutOrUnknown = useMemo(
    () =>
      changedTraits
        ? changedTraits?.some((tokenId) => {
            const traitSale = traitSalesByTokenId[tokenId];
            return traitSale ? traitSale.counter >= traitSale.maxSupply : true;
          })
        : false,
    [changedTraits, traitSalesByTokenId]
  );

  const refreshData = useCallback(() => {
    readHoneyBalance();
    fetchTraitsInWallet();
    fetchTraitSales();
  }, [readHoneyBalance, fetchTraitsInWallet, fetchTraitSales]);

  const handleBuy = useCallback(
    (tokenId: number) => {
      setSelectedTraits([tokenId]);
      setIsPurchasePopupOpen.on();
    },
    [setSelectedTraits, setIsPurchasePopupOpen]
  );

  const handleBuyAll = useCallback(() => {
    if (changedTraits) {
      setSelectedTraits(changedTraits);
      setIsPurchasePopupOpen.on();
    }
  }, [changedTraits, setSelectedTraits, setIsPurchasePopupOpen]);

  const handlePurchaseSuccess = useCallback(() => {
    setIsPurchasePopupOpen.off();

    if (isOwner) {
      setIsStakePopupOpen.on();
    } else {
      refreshData();
    }
  }, [isOwner, setIsPurchasePopupOpen, setIsStakePopupOpen, refreshData]);

  const handleStakePopupClose = useCallback(() => {
    refreshData();
    setIsStakePopupOpen.off();
  }, [refreshData, setIsStakePopupOpen]);

  const handleStakeSuccess = useCallback(() => {
    setIsStakePopupOpen.off();
    setIsStakeSuccessPopupOpen.on();
  }, [setIsStakePopupOpen, setIsStakeSuccessPopupOpen]);

  return tokenId && traitSalesByTokenId ? (
    <>
      {changedTraits && changedTraits.length > 0 && (
        <Box w="full">
          <TraitsList
            title={translate("traitsBag:title")}
            borderBottomRadius={isMultiple ? "none" : undefined}
          >
            {changedTraits?.map((tokenId) => {
              const traitSale = traitSalesByTokenId[tokenId];

              if (!traitSale) {
                return null;
              }

              const isFree = traitSale.price.eq("0");
              const isSoldOut = traitSale.counter >= traitSale.maxSupply;
              const isTooExpensive =
                !honeyBalance || honeyBalance.lt(traitSale.price || "0");

              return (
                <TraitTile
                  key={tokenId}
                  tokenId={tokenId}
                  supply={[traitSale.counter, traitSale.maxSupply]}
                  name={traitSale.name}
                  price={traitSale.price}
                  canvasSize={{
                    base: "12",
                    sm: "14",
                  }}
                >
                  <HStack spacing="3">
                    <Button
                      size="sm"
                      onClick={() => handleBuy(tokenId)}
                      isDisabled={isTooExpensive || isSoldOut}
                    >
                      {translate(isFree ? "common:get" : "common:buy")}
                    </Button>

                    <IconButton
                      size="sm"
                      aria-label=""
                      icon={<Icon as={FaTimes} />}
                      borderRadius="full"
                      colorScheme="dark"
                      onClick={() => removeTrait(tokenId)}
                    />
                  </HStack>
                </TraitTile>
              );
            })}
          </TraitsList>

          {isMultiple && totalPrice && (
            <Box p="3" pt="0" borderBottomRadius="md" bg="dark.900">
              <Button
                w="full"
                onClick={handleBuyAll}
                isDisabled={isOneSoldOutOrUnknown || isTooExpensiveOrUnknown}
              >
                {translate(
                  isFree ? "traitsBag:submit:free" : "traitsBag:submit",
                  {
                    count: changedTraits.length,
                    price: formatEtherBalance(totalPrice),
                  }
                )}
              </Button>
            </Box>
          )}
        </Box>
      )}

      {selectedTraits && isPurchasePopupOpen && (
        <PurchasePopup
          isOpen
          tokenId={tokenId}
          traitsToBuy={selectedTraits}
          onClose={setIsPurchasePopupOpen.off}
          onSuccess={handlePurchaseSuccess}
        />
      )}

      {selectedTraits && isStakePopupOpen && (
        <StakePopup
          isOpen
          currentTraits={originalTraits}
          traitsToStake={selectedTraits}
          tokenId={tokenId}
          onClose={handleStakePopupClose}
          onSuccess={handleStakeSuccess}
        />
      )}

      {selectedTraits && isStakeSuccessPopupOpen && (
        <StakeSuccessPopup
          isOpen
          title={translate("stake:success:title")}
          description={translate("stake:success:description")}
          tokenId={tokenId}
          withConfetti
          currentTraits={originalTraits}
          newTraits={selectedTraits}
        />
      )}
    </>
  ) : null;
};

export default TraitsBagContainer;
